import React, { useState, useMemo, useContext, memo } from 'react';
import { genAttributes } from '@onward-delivery/core';
import { css } from '@emotion/react';
import { useQuery } from '@apollo/client';
import { Button, Grid } from '@material-ui/core';
import AddressAutocomplete from '@/components/ShipmentForm/ModifiedAddressAutocomplete';
import { colors } from '@/styles';
import {
    autocompleteAddressLine,
    autocompleteLatLngPatch,
    clearUnresolvedAutocompleteLatLngPatch,
    toFullState,
} from '@/utilities/address';
import {
    addressSplit,
    sanitizeEnum,
    VALID_LOCATION_TYPES,
    VALID_DROPOFF_TYPES as VALID_PICKUP_TYPES,
} from '../../../../utilities/processOrders';
import { QUERY_LOCATIONS_BY_CLIENT_ID } from '../../../../graphql/queries';
import { ErrorText, TabRow, TabTitle } from '../../../../blocks';
import { ModalContext } from '../../context';
import {
    clearGeocodingLeg,
    formatGeocodingSuggestion,
    geocodingFromAutocomplete,
    hasReviewGeocodingSuggestion,
    isApproximateGeocodingSuggestion,
} from '../../utils/geocoding';
import {
    StoreSelect,
    NewAddressCheckbox,
    LocationType,
    PickupType,
    StoreDetails,
    OnwardTextField,
    OnwardPhoneInput,
} from '../../../InputFields';

const ContactFields = ({ order, attrs, hasError, hasWarning, isDirty, callbacks, options }) => {
    const [name, phone, email] = attrs;

    return (
        <TabRow>
            <Grid item xs={4}>
                <OnwardTextField
                    fullWidth
                    label="Name"
                    variant="outlined"
                    value={order[name]}
                    onBlur={() => callbacks.makeDirty([name])}
                    onChange={(e) => callbacks.modifyOrder({ [name]: e.target.value })}
                    error={hasError[name] && (options.startDirty || isDirty[name])}
                />
            </Grid>
            <Grid item xs={4}>
                <OnwardPhoneInput
                    debounced={false}
                    fullWidth
                    label="Phone"
                    variant="outlined"
                    value={order[phone]}
                    onBlur={() => callbacks.makeDirty([phone])}
                    onChange={(e, value) => callbacks.modifyOrder({ [phone]: value })}
                    error={hasError[phone] && (options.startDirty || isDirty[phone])}
                    warning={hasWarning[phone]}
                />
            </Grid>
            <Grid item xs={4}>
                <OnwardTextField
                    fullWidth
                    label="Email"
                    variant="outlined"
                    value={order[email]}
                    onBlur={() => callbacks.makeDirty([email])}
                    onChange={(e) => callbacks.modifyOrder({ [email]: e.target.value })}
                />
            </Grid>
        </TabRow>
    );
};

/**
 * Edit Order modal tab for the middle-mile origin: contact, address, and geocoded pin.
 * Takes no props; reads the order and callbacks from `ModalContext`.
 */
const OriginTab = memo(() => {
    const { state: modalState, callbacks } = useContext(ModalContext);
    const { order, isDirty, hasError, hasWarning, options } = modalState;
    const { disableAddressEditing, startDirty } = options;
    const [collapsed, setCollapsed] = useState(false);

    const [hasNewAddressOverride, setHasNewAddress] = useState(null);
    const [storeOverride, setStoreOverride] = useState(null);
    const geocodingLeg = useMemo(() => {
        const leg = genAttributes(order, 'ORIGIN').geocoding;
        if (!leg) throw new Error('genAttributes() must provide geocoding leg metadata; update @onward-delivery/core.');
        return leg;
    }, [order]);
    const geocodingSuggestion = order?.geocoding?.[geocodingLeg]?.suggestion;
    const hasGeocodingSuggestion = hasReviewGeocodingSuggestion(order?.geocoding, geocodingLeg);
    const suggestionLabel = formatGeocodingSuggestion(geocodingSuggestion);
    const isApproximateSuggestion = isApproximateGeocodingSuggestion(geocodingSuggestion);
    const suggestionPrefix = isApproximateSuggestion ? 'Approximate suggested origin pin' : 'Suggested origin pin';
    const suggestionAction = isApproximateSuggestion ? 'Use approximate pin' : 'Use suggested pin';
    const clearCurrentGeocoding = () => clearGeocodingLeg(order.geocoding, geocodingLeg);
    const autocompleteGeocoding = (value, split) =>
        geocodingFromAutocomplete(order.geocoding, geocodingLeg, value, split);
    const applySuggestedPin = () => {
        callbacks.modifyOrder({
            middle_mile_origin_lat: geocodingSuggestion.lat,
            middle_mile_origin_lng: geocodingSuggestion.lng,
            geocoding: clearCurrentGeocoding(),
        });
        callbacks.makeDirty(['middle_mile_origin_lat', 'middle_mile_origin_lng']);
    };

    const { data: locationsData } = useQuery(QUERY_LOCATIONS_BY_CLIENT_ID, {
        variables: {
            client_id: order.shipper_id,
        },
        onError: (e) => {
            callbacks.onError(e);
        },
    });

    const locations = useMemo(() => {
        return locationsData?.locations || [];
    }, [locationsData]);

    const store = useMemo(() => {
        if (!storeOverride && locations.length) {
            const match = locations.findIndex(
                (location) =>
                    location.address === order.middle_mile_origin_address ||
                    (location.location_name && location.location_name === order.chosen_store_name)
            );
            return match > -1 ? match : null;
        }
        return storeOverride;
    }, [storeOverride, order, locations]);

    const hasNewAddress = useMemo(() => {
        let hasNewAddress = false;
        if (hasNewAddressOverride !== null) {
            hasNewAddress = hasNewAddressOverride;
        } else {
            hasNewAddress =
                (locations?.length || 0) === 0 ||
                ((order.middle_mile_origin_address?.length || 0) > 0 && store === null);
        }

        return hasNewAddress;
    }, [hasNewAddressOverride, order, store, hasError, locations]);

    const handleStoreSelect = (e) => {
        setStoreOverride(e.target.value);
        let _store = locations[e.target.value];

        const [address1, address2] = addressSplit(_store.business_address);
        callbacks.modifyOrder({
            middle_mile_origin_address: address1,
            middle_mile_origin_unit: address2,
            middle_mile_origin_city: _store.business_city,
            middle_mile_origin_state: _store.business_state,
            middle_mile_origin_zip: _store.business_zip,
            ...(_store.lat != null && _store.lng != null
                ? { middle_mile_origin_lat: _store.lat, middle_mile_origin_lng: _store.lng }
                : {}),
            middle_mile_origin_location: sanitizeEnum(_store.location_type, VALID_PICKUP_TYPES) || 'Business',
            middle_mile_origin_location_type: sanitizeEnum(_store.location_info, VALID_LOCATION_TYPES) || 'dock',
            geocoding: clearCurrentGeocoding(),
        });

        callbacks.makeDirty([
            'middle_mile_origin_address',
            'middle_mile_origin_city',
            'middle_mile_origin_state',
            'middle_mile_origin_zip',
            'middle_mile_origin_lat',
            'middle_mile_origin_lng',
            'middle_mile_origin_location',
            'middle_mile_origin_location_type',
        ]);
    };

    const hasAddressFieldError = [
        'middle_mile_origin_address',
        'middle_mile_origin_city',
        'middle_mile_origin_state',
        'middle_mile_origin_zip',
    ].some((attr) => hasError[attr] && (startDirty || isDirty[attr]));

    let addressError = null;
    if (hasAddressFieldError || hasGeocodingSuggestion) {
        addressError = (
            <Grid
                css={css`
                    color: ${colors.reds[1]};
                    margin-bottom: 4px;
                `}
                container
                direction="column"
            >
                <ErrorText>{hasAddressFieldError ? 'Address is invalid' : 'Origin address needs review'}</ErrorText>
                {hasGeocodingSuggestion ? (
                    <Grid
                        item
                        css={css`
                            margin-top: 4px;
                        `}
                    >
                        {suggestionPrefix}: {suggestionLabel || 'approximate location'}
                        <Button
                            color="primary"
                            disabled={disableAddressEditing}
                            onClick={applySuggestedPin}
                            size="small"
                        >
                            {suggestionAction}
                        </Button>
                    </Grid>
                ) : null}
            </Grid>
        );
    }

    return (
        <>
            <TabTitle collapsed={collapsed} callbacks={{ setCollapsed }}>
                Origin
            </TabTitle>
            {collapsed ? null : (
                <>
                    <ContactFields
                        order={order}
                        isDirty={isDirty}
                        attrs={['middle_mile_origin_name', 'middle_mile_origin_phone', 'middle_mile_origin_email']}
                        hasError={hasError}
                        hasWarning={hasWarning}
                        callbacks={callbacks}
                        options={options}
                    />

                    <TabRow>
                        <Grid item xs={9}>
                            <StoreSelect
                                value={store}
                                onChange={handleStoreSelect}
                                error={
                                    hasError.middle_mile_origin_address &&
                                    !hasNewAddress &&
                                    (startDirty || isDirty.middle_mile_origin_address)
                                }
                                storeList={locations || []}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <NewAddressCheckbox
                                hasNewAddress={hasNewAddress}
                                disabled={locations?.length === 0}
                                onChange={() => {
                                    const next = !hasNewAddress;
                                    setHasNewAddress(next);
                                }}
                            />
                        </Grid>
                    </TabRow>
                    {addressError ? addressError : null}

                    {!hasNewAddress && store !== null ? (
                        <Grid
                            container
                            direction="column"
                            css={css`
                                margin-bottom: 12px;
                            `}
                        >
                            <StoreDetails
                                storeName={locations?.[store]?.location_name}
                                fullAddress={[
                                    order.middle_mile_origin_address,
                                    order.middle_mile_origin_unit,
                                    order.middle_mile_origin_city,
                                    order.middle_mile_origin_state,
                                    order.middle_mile_origin_zip,
                                ]
                                    .filter((x) => x)
                                    .join(', ')}
                                locationType={order.middle_mile_origin_location}
                                pickupType={order.middle_mile_origin_location_type}
                            />
                        </Grid>
                    ) : (
                        <>
                            <TabRow>
                                <Grid item xs={9}>
                                    <AddressAutocomplete
                                        disabled={disableAddressEditing}
                                        state={{
                                            street: order.middle_mile_origin_address,
                                            city: order.middle_mile_origin_city,
                                            state: order.middle_mile_origin_state,
                                            zip: order.middle_mile_origin_zip,
                                        }}
                                        handleAddressUpdate={(value, split) => {
                                            callbacks.modifyOrder({
                                                middle_mile_origin_address: autocompleteAddressLine(value, split),
                                                middle_mile_origin_city: split.city,
                                                middle_mile_origin_state: split.state,
                                                middle_mile_origin_zip: split.zip,
                                                ...autocompleteLatLngPatch(
                                                    split,
                                                    'middle_mile_origin_lat',
                                                    'middle_mile_origin_lng'
                                                ),
                                                ...clearUnresolvedAutocompleteLatLngPatch(
                                                    split,
                                                    'middle_mile_origin_lat',
                                                    'middle_mile_origin_lng'
                                                ),
                                                geocoding: autocompleteGeocoding(value, split),
                                            });

                                            callbacks.makeDirty([
                                                'middle_mile_origin_address',
                                                'middle_mile_origin_city',
                                                'middle_mile_origin_state',
                                                'middle_mile_origin_zip',
                                                'middle_mile_origin_lat',
                                                'middle_mile_origin_lng',
                                            ]);
                                        }}
                                        error={
                                            hasNewAddress &&
                                            [
                                                'middle_mile_origin_address',
                                                'middle_mile_origin_city',
                                                'middle_mile_origin_state',
                                                'middle_mile_origin_zip',
                                            ].some((attr) => hasError[attr] && (startDirty || isDirty[attr]))
                                        }
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <OnwardTextField
                                        variant="outlined"
                                        color="primary"
                                        label="Unit/Suite #"
                                        value={order.middle_mile_origin_unit}
                                        onChange={(e) =>
                                            callbacks.modifyOrder({
                                                middle_mile_origin_unit: e.target.value,
                                                geocoding: clearCurrentGeocoding(),
                                            })
                                        }
                                        fullWidth
                                    />
                                </Grid>
                            </TabRow>
                            <TabRow>
                                <Grid item xs={4}>
                                    <OnwardTextField
                                        fullWidth
                                        label="City"
                                        variant="outlined"
                                        color="primary"
                                        value={order.middle_mile_origin_city}
                                        error={
                                            hasError.middle_mile_origin_city &&
                                            (startDirty || isDirty.middle_mile_origin_city)
                                        }
                                        disabled={true}
                                        onBlur={() => callbacks.makeDirty(['middle_mile_origin_city'])}
                                        onChange={(e) =>
                                            callbacks.modifyOrder({
                                                middle_mile_origin_city: e.target.value,
                                                geocoding: clearCurrentGeocoding(),
                                            })
                                        }
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <OnwardTextField
                                        fullWidth
                                        variant="outlined"
                                        color="primary"
                                        label="State"
                                        disabled={true}
                                        error={
                                            hasError.middle_mile_origin_state &&
                                            (startDirty || isDirty.middle_mile_origin_state)
                                        }
                                        value={toFullState(order.middle_mile_origin_state)}
                                        onBlur={() => callbacks.makeDirty(['middle_mile_origin_state'])}
                                        onChange={(e) =>
                                            callbacks.modifyOrder({
                                                middle_mile_origin_state: e.target.value,
                                                geocoding: clearCurrentGeocoding(),
                                            })
                                        }
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <OnwardTextField
                                        fullWidth
                                        variant="outlined"
                                        color="primary"
                                        label="Zip"
                                        value={order.middle_mile_origin_zip}
                                        error={
                                            hasError.middle_mile_origin_zip &&
                                            (startDirty || isDirty.middle_mile_origin_zip)
                                        }
                                        onBlur={() => callbacks.makeDirty(['middle_mile_origin_zip'])}
                                        onChange={(e) =>
                                            callbacks.modifyOrder({
                                                middle_mile_origin_zip: e.target.value,
                                                geocoding: clearCurrentGeocoding(),
                                            })
                                        }
                                    />
                                </Grid>
                            </TabRow>

                            <TabRow>
                                <Grid item xs={6}>
                                    <LocationType
                                        value={order.middle_mile_origin_location}
                                        onChange={(e) => {
                                            callbacks.modifyOrder({ middle_mile_origin_location: e.target.value });
                                            callbacks.makeDirty(['middle_mile_origin_location']);
                                        }}
                                        error={
                                            hasError.middle_mile_origin_location &&
                                            (startDirty || isDirty.middle_mile_origin_location)
                                        }
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <PickupType
                                        disabled={
                                            !order.middle_mile_origin_location ||
                                            order.middle_mile_origin_location.length === 0
                                        }
                                        value={order.middle_mile_origin_location_type}
                                        locationType={order.middle_mile_origin_location}
                                        onChange={(e) => {
                                            callbacks.modifyOrder({ middle_mile_origin_location_type: e.target.value });
                                            callbacks.makeDirty(['middle_mile_origin_location_type']);
                                        }}
                                        error={
                                            hasError.middle_mile_origin_location_type &&
                                            (startDirty || isDirty.middle_mile_origin_location_type)
                                        }
                                    />
                                </Grid>
                            </TabRow>
                        </>
                    )}
                </>
            )}
        </>
    );
});

export default OriginTab;
